import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";

import { AlertCircle, JusticeScale } from "@/assets/icons";
import { Badge, Card } from "@/atoms";
import { BadgeStateEnum } from "@/enums";
import { AlfredReconciliationDataInterface } from "@/interfaces";
import { generateValidClassNameHandler } from "@/lib/utils";
import { ReconciliationJobType } from "@/types";
import { Button } from "@/ui/button";

const getStatusBadgeState = (status: ReconciliationJobType) => {
    switch (status) {
    case "COMPLETED":
        return BadgeStateEnum.SUCCESS;
    case "RUNNING":
        return BadgeStateEnum.INPROGRESS;
    case "FAILED":
        return BadgeStateEnum.DANGER;
    case "PENDING":
        return BadgeStateEnum.LIGHT;
    default:
        return BadgeStateEnum.DEFAULT;
    }
};

const getStatusBorderColor = (status: ReconciliationJobType) => {
    switch (status) {
    case "COMPLETED":
        return "border-t-green-500";
    case "RUNNING":
        return "border-t-blue-500";
    case "FAILED":
        return "border-t-red-500";
    case "PENDING":
        return "border-t-gray-400";
    default:
        return "border-t-gray-300";
    }
};

const getProgressColor = (status: ReconciliationJobType) => {
    switch (status) {
    case "COMPLETED":
        return " bg-green-500";
    case "RUNNING":
        return " bg-blue-500";
    case "FAILED":
        return " bg-red-500";
    default:
        return " bg-gray-400";
    }
};

const formatLastRun = (dateStr?: string) => {
    if (!dateStr) return "Never run";
    try {
        return format(
            parseISO(dateStr),
            "MMM d, yyyy 'at' h:mm a",
        );
    } catch {
        return dateStr;
    }
};

export const AlfredReconciliationCard = ({
    className,
    data,
    onRunNow,
} : {
    className?: string,
    data: AlfredReconciliationDataInterface,
    onRunNow?: () => void,
}) => {
    const navigate = useNavigate();

    const handleViewDetails = () => {
        navigate(`/reconciliations/${data.id}`);
    };

    const handleRunNow = () => {
        if (onRunNow) onRunNow();
    };

    const matchedPercent = data.total > 0 ? Math.round((data.matched / data.total) * 100) : 0;
    const reviewPercent = data.total > 0 ? Math.round((data.review / data.total) * 100) : 0;
    const exceptionsPercent = data.total > 0 ? Math.round((data.exceptions / data.total) * 100) : 0;

    const isRunning = data.status === "RUNNING";
    const isFailed = data.status === "FAILED";

    const headerContent = (
        <>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    <JusticeScale className="size-5 text-primary" />
                    <span className="text-sm font-medium leading-tight">
                        {data.name}
                    </span>
                </div>
                <Badge state={getStatusBadgeState(data.status)}>
                    {data.status}
                </Badge>
            </div>
            <p className="text-xs text-gray-500">
                Last run:
                {" "}
                {formatLastRun(data.lastRun)}
            </p>
        </>
    );

    const cardContent = (
        <>
            <div className="space-y-3">
                {/* Progress Section */}
                <div className="space-y-1">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Progress</span>
                        <span className="text-sm font-medium">
                            {data.progress}
                            % Complete
                        </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                            style={{ width: `${data.progress}%` }}
                            className={generateValidClassNameHandler(
                                "h-full transition-all",
                                getProgressColor(data.status),
                            )}
                        />
                    </div>
                </div>

                {/* Breakdown Section */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Matched</span>
                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
                                <div
                                    className="h-full bg-green-500"
                                    style={{ width: `${matchedPercent}%` }}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">{data.matched.toLocaleString()}</span>
                            <span className="text-xs text-gray-500">
                                {matchedPercent}
                                %
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Review Needed</span>
                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
                                <div
                                    className="h-full bg-amber-500"
                                    style={{ width: `${reviewPercent}%` }}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">{data.review.toLocaleString()}</span>
                            <span className="text-xs text-gray-500">
                                {reviewPercent}
                                %
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Exceptions</span>
                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
                                <div
                                    className="h-full bg-red-500"
                                    style={{ width: `${exceptionsPercent}%` }}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">{data.exceptions.toLocaleString()}</span>
                            <span className="text-xs text-gray-500">
                                {exceptionsPercent}
                                %
                            </span>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {isFailed && data.errorMessage && (
                    <div className="flex items-start gap-2 rounded bg-red-50 p-2">
                        <AlertCircle className="size-4 shrink-0 text-red-500" />
                        <span className="text-xs text-red-600">{data.errorMessage}</span>
                    </div>
                )}
            </div>

            {/* Action buttons as part of content (Option 3) */}
            <div className="mt-3 flex gap-2 border-t pt-3">
                <Button
                    className="flex-1"
                    size="sm"
                    onClick={handleViewDetails}
                >
                    View Details
                </Button>
                <Button
                    className="flex-1"
                    disabled={isRunning}
                    size="sm"
                    variant="outline"
                    onClick={handleRunNow}
                >
                    {isRunning ? "Running..." : "Run Now"}
                </Button>
            </div>
        </>
    );

    return (
        <Card
            content={cardContent}
            contentClassName="pb-3 pt-0"
            headerClassName="pb-2"
            otherHeaderData={headerContent}
            className={generateValidClassNameHandler(
                "w-full border-t-4 shadow-sm",
                getStatusBorderColor(data.status),
                className,
            )}
        />
    );
};
